<template>
  <NuxtLayout name="main-view" :headline="$t('dbc-btn-text-change-payment')">
    <Card :pt="formCardPassThrough">
      <template #content>
        <FormKit
          :actions="false"
          type="form"
          form-class="rounded-lg"
          #default="{ state: { valid }, value }"
          @submit="isSepaVerify ? verifySepa() : updatePaymentData()"
          :aria-invalid="errorMessage"
        >
          <Skeleton
            v-if="paymentMethodOptions.length === 0"
            width="100%"
            height="80px"
            borderRadius="16px"
          />
          <FormKit
            v-else-if="!isSepaVerify"
            v-model="paymentMethod"
            type="radio"
            :label="$t('dbc-label-choose-payment-method')"
            :options="paymentMethodOptions"
            name="paymentMethod"
            id="paymentMethod"
            validation="required"
            validation-visibility="blur"
          />

          <hr
            v-if="paymentMethod && !isSepaVerify"
            class="w-full m-0 border-solid border-0 border-t border-light"
          />

          <DbcBanner
            v-if="paymentMethod && !isSepaVerify && !errorMessage"
            severity="info"
            :message="$t('dbc-text-payment-verify-info')"
          />

          <DbcBanner
            v-if="isSepaVerify"
            severity="info"
            :message="$t('dbc-text-verify-sepa')"
          />

          <DbcBanner
            v-if="errorMessage"
            id="errorBanner"
            :severity="'warn'"
            :message="errorMessage"
          />

          <div
            v-if="!isSepaVerify"
            v-bind:style="{ height: paymentWidgetHeight }"
            id="paymentWidgetContainer"
          ></div>

          <div class="flex flex-col gap-2xs">
            <FormKit
              v-if="errorMessage === ''"
              type="submit"
              id="submit"
              :suffix-icon="isSepaVerify ? 'navigationLink' : null"
              :label="
                !valid
                  ? $t('dbc-text-fill-in-mandatory-field')
                  : isSepaVerify
                  ? $t('dbc-btn-verify')
                  : $t('dbc-btn-check')
              "
              :disabled="!valid || isLoading || isDisabled"
              :loading="isLoading"
              :aria-label="
                !valid
                  ? $t('dbc-text-fill-in-mandatory-fields')
                  : !isLoading
                  ? isSepaVerify
                    ? $t('dbc-btn-verify')
                    : $t('dbc-btn-check')
                  : $t('dbc-text-loading')
              "
            />
            <FormKit
              type="button"
              id="abort"
              @click="
                router.push({
                  path: usePagePath('payment'),
                })
              "
              :label="$t('dbc-btn-abort')"
            />
          </div>
        </FormKit>
      </template>
    </Card>
  </NuxtLayout>
</template>

<script setup lang="ts">
import { useCustomerStore } from '../../../../stores/customer'

const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const paymentMethod = ref('')

const {
  isDisabled,
  isLoading,
  isSepaVerify,
  errorMessage,
  paymentWidgetHeight,
  onVerifySepaComplete,
  verifySepa,
  updatePaymentData,
} = usePaymentWidget(paymentMethod)

onMounted(() => {
  const channel = new BroadcastChannel('sepa-verify-channel')
  channel.addEventListener('message', (event) => {
    onVerifySepaComplete(event.data)
  })

  window.addEventListener('sepa-verify-complete', (event: any) => {
    const sepaVerifyData = JSON.parse(window.atob(event.detail.sepaVerifyData))
    onVerifySepaComplete(sepaVerifyData)
  })

  if (route.query['sepa-verify']) {
    channel.postMessage({
      result: route.query['sepa-verify'],
      timestamp: route.query['ts'],
    })
    window.close()
  }
})

const customerStore = useCustomerStore()
const { tariff } = storeToRefs(customerStore)

const paymentMethodOptions = ref<RadioOption[]>([])

setupPaymentMethodOptions()

watch(tariff, () => {
  setupPaymentMethodOptions()
})

function setupPaymentMethodOptions() {
  if (tariff.value?.paymentMethods.includes('creditCard')) {
    paymentMethodOptions.value.push({
      value: 'creditcard',
      label: t('dbc-label-creditcard'),
    })
  }
  if (tariff.value?.paymentMethods.includes('sepaDirectDebit')) {
    paymentMethodOptions.value.push({
      value: 'sepa',
      label: t('dbc-label-sepa'),
    })
  }
}
</script>
